import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Eye, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import VisitorsDayDialog from "./VisitorsDayDialog";

/**
 * UniqueVisitorsCard — site-wide daily unique sessions tile.
 *
 * Shows today's unique-session count + 7-day sparkline + delta vs prior
 * 6-day average. Clicking the tile opens the hour-by-hour drill-down
 * dialog (VisitorsDayDialog). Mirrors the SignupTrendTile visual
 * contract so the KPI row stays cohesive.
 *
 * Data source: GET /api/admin/daily-uniques returns
 *   { days, series: [{date, unique_sessions, page_views}], today, yesterday }
 */
export default function UniqueVisitorsCard() {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/daily-uniques?days=7")
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setErrored(true));
    return () => { mounted = false; };
  }, []);

  const days = (data?.series || []).map((d) => ({ ...d, value: d.unique_sessions }));
  const today = days.length ? days[days.length - 1].value : 0;
  const prior = days.slice(0, -1).map((d) => d.value);
  const priorAvgRaw = prior.length ? prior.reduce((a, b) => a + b, 0) / prior.length : 0;
  const delta = priorAvgRaw > 0 ? Math.round(((today - priorAvgRaw) / priorAvgRaw) * 100) : null;
  const total = days.reduce((a, b) => a + b.value, 0);
  const avgPerDay = days.length ? Math.round((total / days.length) * 10) / 10 : 0;

  const max = Math.max(1, ...days.map((d) => d.value));
  const w = 100;
  const h = 28;
  const stepX = days.length > 1 ? w / (days.length - 1) : w;
  const points = days.map((d, i) => [i * stepX, h - (d.value / max) * (h - 4) - 2]);
  const linePath = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = points.length
    ? `${linePath} L${points[points.length - 1][0]},${h} L0,${h} Z`
    : "";

  const tone = today > 0 ? "primary" : "muted";
  const deltaTone = delta == null ? "muted" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          "text-left rounded-2xl border bg-card p-4 transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/40 " +
          (tone === "primary" ? "border-primary/40 ring-1 ring-primary/10" : "border-border")
        }
        data-testid="admin-kpi-unique-visitors"
        title="Click for hourly breakdown"
      >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Visitors today</div>
        <Eye weight="duotone" className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div className="font-display font-bold text-2xl leading-none">{today}</div>
        {deltaTone !== "muted" && (
          <span
            className={
              "inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 " +
              (deltaTone === "up"
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : deltaTone === "down"
                ? "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                : "bg-muted text-muted-foreground")
            }
            title={`vs. prior 6-day avg (${avgPerDay}/day)`}
            data-testid="admin-kpi-unique-visitors-delta"
          >
            {deltaTone === "up" ? (
              <ArrowUp weight="bold" className="h-2.5 w-2.5" />
            ) : (
              <ArrowDown weight="bold" className="h-2.5 w-2.5" />
            )}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
      {!errored && days.length > 0 && (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="mt-2 w-full h-7 overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="uniqueVisitorsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(234 88 12)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="rgb(234 88 12)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaPath && <path d={areaPath} fill="url(#uniqueVisitorsFill)" />}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="rgb(234 88 12)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {points.length > 0 && (
            <circle
              cx={points[points.length - 1][0]}
              cy={points[points.length - 1][1]}
              r="2"
              fill="rgb(234 88 12)"
            />
          )}
        </svg>
      )}
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        7-day · avg {avgPerDay}/day
      </div>
      </button>
      <VisitorsDayDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
